import { isNumber } from '@xemplo/type-guard';

import { ParseCssVarProps, ParseSizeValueProps } from './css-var.types';

/**
 * Helper that handles parsing a size that's convertable to
 * a pixel value. If the string is something like `auto | 100% | 1em`
 * it gets returned as is.
 */
export function parseCssSize(size?: string | number): string {
  if (size == null) return 'auto';
  return isNumber(size) ? `${size}px` : size;
}

/**
 * Simple helper to normalize the key name for a css variable.
 * It's a little convenience so you can simply type something
 * like: input-width or bg-color and that will be parsed to
 * --input-width or --bg-color
 */
export function normalizeCssKey(key: string): string {
  return key.startsWith('--') ? key : `--${key}`;
}

/**
 * Tiny helper to deal with css size variables.
 * If the value is not provided, it defaults to 'auto'.
 *
 * It should be used to allow simpler implementation of numbers
 * this is not designed to be a catch all for css parsing.
 *
 * NOTE: Key will be normalized and allows key with or without '--' prefix.
 *
 * @param key The key for a size css variable. e.g.: --input-width or input-width
 * @param value
 * @returns A React CSSProperties object with the parsed css variable.
 */
export function parseCssSizeVar(key: string, value?: string | number) {
  const normalizedKey = normalizeCssKey(key);
  const parsed = parseCssSize(value);
  return { [normalizedKey]: parsed } as React.CSSProperties;
}

/**
 * Parses a size into a css variable object, with support for a custom unit
 * and a fallback default value.
 *
 * Returns an empty object when neither `value` nor `defaultValue` is provided,
 * so the variable is simply omitted.
 *
 * @param props See {@link ParseCssVarProps}.
 * @returns A React CSSProperties object with the parsed css variable.
 */
export function parseSizeCssVar({
  cssVar,
  value,
  unit = 'px',
  defaultValue,
}: ParseCssVarProps): React.CSSProperties {
  if (value == null && defaultValue == null) return {};
  const resolved = value ?? defaultValue;
  if (isNumber(resolved)) return { [cssVar]: `${resolved}${unit}` };
  return { [cssVar]: resolved };
}

/**
 * Parses a size value into a css-ready string, appending `unit` (default `px`)
 * to numeric values and returning non-numeric values as-is.
 *
 * @param props See {@link ParseSizeValueProps}.
 * @returns The parsed size string, or `undefined` when no value is provided.
 */
export function parseSizeValue({
  value,
  unit = 'px',
}: ParseSizeValueProps): string | undefined {
  if (isNumber(value)) return `${value}${unit}`;
  return value;
}
